import React, { useEffect, useState } from 'react';
import { Navigate, useParams } from 'react-router-dom';
import PropTypes from 'prop-types';
import _get from 'lodash-es/get';
import _noop from 'lodash-es/noop';
import Breadcrumb from 'sushiweb/Breadcrumb/Breadcrumb';
import H4 from 'sushiweb/Typography/Heading/H4';
import Layout from 'sushiweb/Layout/Layout';
import Button from 'sushiweb/Button/Button';
import DoubleArrowRight from 'sushiweb/Icons/all/DoubleArrowRight';
import Spinner from 'sushiweb/Spinner/Spinner';
import red from 'sushiweb/tokens/color/red';
import grey from 'sushiweb/tokens/color/grey';
import { P } from 'sushiweb/Typography/Text';

import { PAYMENTS_ROUTE } from '../../../constants/routes';
import { GET_PAYMENT_CODE } from '../../../constants/urls';
import zfetch from '../../common/utils/zfetch';
import {
  BreadCrumbWrapper,
  DashboardWrapper,
  HeadWrapper,
  LayoutContainers,
  OnlinePaymentHeader,
  SpinnerHolder,
  StyledButton,
} from '../../common/styles';
import PaymentKit from './PaymentKit';
import { formatAmountInRupees } from '../../../helpers';
import { CheckoutBtnContainer } from '../styles';
import {
  ENTERPRISE_BALANCE_SERVICE_TYPE,
  ENTERPRISE_LIMIT_SERVICE_TYPE,
} from './PaymentKit/Constants';
import ErrorPage from '../../common/components/ErrorPage/ErrorPage';
import {
  ONLINE_PAYMENT_ERROR,
  REFRESH_LABEL,
  applicableFlowTypes,
  currentFlowTypeEnum,
  invoiceAmountGreaterThanPending,
} from '../../common/components/constants';
import NotFound from '../../common/components/NotFound';
import { RefreshIcon } from '../../../components/SVGs/commonSVGs';

const OnlinePaymentCheckoutPage = ({
  currentFlow,
  isOnlinePaymentsEnabled,
  selectedWorkspace,
  paymentsInfo,
  getOnlinePaymentInfo,
  fetchingOnlinePaymentInfo,
  fetchedOnlinePaymentInfo,
  refreshAuth,
  refreshingAuth,
  invoiceDetails,
  getInvoiceDetails,
  fetchingInvoiceDetails,
  fetchedInvoiceDetails,
}) => {
  const { startDate, endDate } = useParams();
  const [redirectPage, setRedirectPage] = useState(false);
  const [paymentSDKData, setPaymentSDKData] = useState({});
  const [accessCode, setAccessCode] = useState('');
  const amountBalance = Number.parseFloat(
    localStorage.onlinePaymentAmount ?? 0,
  );
  const [isAccessCodeFetched, setIsAccessCodeFetched] = useState(true);
  const [loadingCheckout, setLoadingCheckout] = useState(false);
  const [paymentKitVisibility, setPaymentKitVisibility] = useState(false);
  const [isCheckoutStarted, setIsCheckoutStarted] = useState(false);
  const [isCheckoutEnded, setIsCheckoutEnded] = useState(false);
  const [pageLoading, setPageLoading] = useState(false);
  const [invoiceErrorMsg, setInvoiceErrorMsg] = useState('');
  const refreshPage = () => {
    refreshAuth();
    setIsCheckoutStarted(false);
    setLoadingCheckout(false);
    if (currentFlow === currentFlowTypeEnum.FLOW_TYPE_LIMIT) {
      getInvoiceDetails({ startDate, endDate, flowType: currentFlow });
    }
  };
  const closePage = () => {
    setRedirectPage(true);
  };
  const checkoutPayment = () => {
    setIsCheckoutStarted(true);
    setLoadingCheckout(true);
  };
  if (!isOnlinePaymentsEnabled) {
    return (
      <DashboardWrapper>
        <NotFound />
      </DashboardWrapper>
    );
  }

  useEffect(() => {
    if (currentFlow === currentFlowTypeEnum.FLOW_TYPE_LIMIT) {
      getInvoiceDetails({ startDate, endDate, flowType: currentFlow });
    }
  }, [currentFlow]);

  useEffect(() => {
    if (currentFlow === currentFlowTypeEnum.FLOW_TYPE_LIMIT) {
      setPageLoading(
        refreshingAuth || fetchingOnlinePaymentInfo || fetchingInvoiceDetails,
      );
    } else {
      setPageLoading(refreshingAuth || fetchingOnlinePaymentInfo);
    }
  }, [
    refreshingAuth,
    fetchingOnlinePaymentInfo,
    fetchingInvoiceDetails,
    currentFlow,
  ]);

  useEffect(() => {
    if (
      currentFlow === currentFlowTypeEnum.FLOW_TYPE_LIMIT &&
      !fetchingInvoiceDetails &&
      fetchedInvoiceDetails
    ) {
      if (amountBalance > invoiceDetails.pending_amount) {
        setInvoiceErrorMsg(invoiceAmountGreaterThanPending);
      }
      // else if (amountBalance > 1000000) {
      //   setInvoiceErrorMsg(invoiceAmountGreaterThan10L);
      // }
      else {
        setInvoiceErrorMsg('');
      }
    }
  }, [fetchingInvoiceDetails, fetchedInvoiceDetails]);
  useEffect(() => {
    if (selectedWorkspace && selectedWorkspace.workspace_id)
      getOnlinePaymentInfo({
        workspaceID: selectedWorkspace.workspace_id,
        flowType:
          currentFlow === currentFlowTypeEnum.FLOW_TYPE_PPI
            ? ENTERPRISE_BALANCE_SERVICE_TYPE
            : ENTERPRISE_LIMIT_SERVICE_TYPE,
      });
  }, [selectedWorkspace, currentFlow]);
  useEffect(() => {
    if (
      !fetchingOnlinePaymentInfo &&
      fetchedOnlinePaymentInfo &&
      paymentsInfo
    ) {
      setPaymentSDKData({
        amount: amountBalance,
        country_id: paymentsInfo.country_id ?? 1,
        service_type: paymentsInfo.service_type ?? '',
        company_id: paymentsInfo.company_id ?? '1',
        online_payments_flag: paymentsInfo.online_payments_flag ?? 1,
        user_details: {
          user_email: paymentsInfo?.user_details?.email ?? '',
          phone: paymentsInfo?.user_details?.phone ?? '',
        },
      });
    }
  }, [fetchingOnlinePaymentInfo]);
  useEffect(() => {
    if (!redirectPage) {
      zfetch(GET_PAYMENT_CODE, {
        params: {
          service_type:
            currentFlow === currentFlowTypeEnum.FLOW_TYPE_LIMIT
              ? ENTERPRISE_LIMIT_SERVICE_TYPE
              : ENTERPRISE_BALANCE_SERVICE_TYPE,
        },
      })
        .then(res => res.json())
        .then(data => {
          setAccessCode(_get(data, 'token'));
          setIsAccessCodeFetched(true);
        })
        .catch(error => {
          setIsAccessCodeFetched(false);
          console.error(error);
        });
    }
  }, [redirectPage, selectedWorkspace]);

  if (pageLoading) {
    return (
      <DashboardWrapper>
        <SpinnerHolder className='center'>
          <Spinner color={red.z_red} size='medium' />
        </SpinnerHolder>
      </DashboardWrapper>
    );
  }
  if (currentFlow === currentFlowTypeEnum.FLOW_TYPE_LIMIT) {
    if (!fetchedInvoiceDetails)
      return (
        <DashboardWrapper>
          <ErrorPage
            showAction
            actionLabel={REFRESH_LABEL}
            clickAction={refreshPage}
          />
        </DashboardWrapper>
      );
    if (invoiceErrorMsg !== '') {
      return (
        <DashboardWrapper>
          <ErrorPage
            showAction
            errorMsg={invoiceErrorMsg}
            actionLabel={REFRESH_LABEL}
            clickAction={refreshPage}
          />
        </DashboardWrapper>
      );
    }
  }
  return redirectPage ? (
    <Navigate to={PAYMENTS_ROUTE} replace />
  ) : (
    <DashboardWrapper>
      <BreadCrumbWrapper>
        <Breadcrumb>
          <Breadcrumb.Link linkTo='/'>Home</Breadcrumb.Link>
          <Breadcrumb.Link linkTo='/payments'>Payments</Breadcrumb.Link>
        </Breadcrumb>
      </BreadCrumbWrapper>
      <HeadWrapper>
        {currentFlow === currentFlowTypeEnum.FLOW_TYPE_LIMIT ? (
          <OnlinePaymentHeader>
            <H4>
              Paying amount: &nbsp;
              <div id='amount'>
                {formatAmountInRupees(Number.parseFloat(amountBalance))}
              </div>
            </H4>
            <P>{invoiceDetails.invoice_id}</P>
          </OnlinePaymentHeader>
        ) : (
          <OnlinePaymentHeader>
            <H4>
              Amount to be transferred: &nbsp;
              <div id='amount'>
                {formatAmountInRupees(Number.parseFloat(amountBalance))}
              </div>
            </H4>
          </OnlinePaymentHeader>
        )}
        <StyledButton
          icon={<RefreshIcon />}
          iconAlign='right'
          onClick={refreshPage}
          style={{
            fontWeight: 400,
            fontSize: '14px',
            color: 'black',
            backgroundColor: 'white',
            border: `0.5px solid ${grey.z200}`,
          }}
        >
          Refresh
        </StyledButton>
      </HeadWrapper>
      <LayoutContainers style={{ padding: '30px 0px 0px 0px' }}>
        <Layout gap={20}>
          <Layout.Item colD={12} colT={12} colM={12}>
            {fetchedOnlinePaymentInfo && isAccessCodeFetched ? (
              <PaymentKit
                paymentKitVisibility={paymentKitVisibility}
                setPaymentKitVisibility={setPaymentKitVisibility}
                paymentSdkData={paymentSDKData}
                accessCode={accessCode}
                isCheckoutStarted={isCheckoutStarted}
                isCheckoutEnded={isCheckoutEnded}
                setIsCheckoutEnded={setIsCheckoutEnded}
                initiateCheckout={checkoutPayment}
                setIsCheckoutStarted={setIsCheckoutStarted}
                setLoadingCheckout={setLoadingCheckout}
              />
            ) : (
              <ErrorPage
                showAction
                actionLabel={ONLINE_PAYMENT_ERROR}
                clickAction={closePage}
              />
            )}
          </Layout.Item>
          <CheckoutBtnContainer>
            <Button
              onClick={checkoutPayment}
              loading={loadingCheckout}
              style={{ paddingRight: '30px' }}
              icon={<DoubleArrowRight />}
              iconAlign='right'
            >
              {' '}
              Pay Now
              {' '}
            </Button>
          </CheckoutBtnContainer>
        </Layout>
      </LayoutContainers>
    </DashboardWrapper>
  );
};

OnlinePaymentCheckoutPage.propTypes = {
  currentFlow: PropTypes.oneOf(applicableFlowTypes),
  isOnlinePaymentsEnabled: PropTypes.bool,
  selectedWorkspace: PropTypes.objectOf(PropTypes.any),
  getOnlinePaymentInfo: PropTypes.func,
  paymentsInfo: PropTypes.objectOf(PropTypes.any),
  fetchingOnlinePaymentInfo: PropTypes.bool,
  fetchedOnlinePaymentInfo: PropTypes.bool,
  refreshAuth: PropTypes.func,
  refreshingAuth: PropTypes.bool,
  invoiceDetails: PropTypes.objectOf(PropTypes.any),
  getInvoiceDetails: PropTypes.func,
  fetchingInvoiceDetails: PropTypes.bool,
  fetchedInvoiceDetails: PropTypes.bool,
};

OnlinePaymentCheckoutPage.defaultProps = {
  currentFlow: currentFlowTypeEnum.FLOW_TYPE_PPI,
  isOnlinePaymentsEnabled: false,
  selectedWorkspace: {},
  getOnlinePaymentInfo: _noop,
  paymentsInfo: {},
  fetchingOnlinePaymentInfo: true,
  fetchedOnlinePaymentInfo: true,
  refreshAuth: _noop,
  refreshingAuth: false,
  invoiceDetails: {},
  getInvoiceDetails: _noop,
  fetchingInvoiceDetails: false,
  fetchedInvoiceDetails: false,
};

export default OnlinePaymentCheckoutPage;
